import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/home/runner/work/commerce-pwa-studio/commerce-pwa-studio/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "state-management"
    }}>{`State management`}</h1>
    <p>{`State management describes the way the application handles changing state data as a result of user interactions.`}</p>
    <h2 {...{
      "id": "overview"
    }}>{`Overview`}</h2>
    <p>{`An application, such as a PWA storefront, uses state data to render dynamic content to the user.
Interactions, such as clicking on a button or loading the page, modify the state and update the appearance or behavior of the application.`}</p>
    <p>{`For example, a shopper clicks on a button to add an item to the shopping cart.
The application needs a way to add that item to the shopping cart while the shopper continues to browse the application.
It also needs to update the visual components that use shopping cart data to reflect the new state.`}</p>
    <h2 {...{
      "id": "local-versus-global-state"
    }}>{`Local versus global state`}</h2>
    <p>{`Local and global are the two different types of state a component can depend on.`}</p>
    <p>{`Local state data is any data scoped within a component or its children components.
This type of data is not shared with a component's parent or peer data.
Otherwise, that data should be `}<a parentName="p" {...{
        "href": "https://reactjs.org/docs/lifting-state-up.html"
      }}>{`lifted`}</a>{`.`}</p>
    <p>{`For example, a button component's disabled state is never used outside that component, so
it is categorized as local state data.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const MyButton = () => {
  const [isDisabled, setIsDisabled] = useState(false);

  const handleClick = useCallback(() => setIsDisabled(true), []);

  return (
    <button onClick={handleClick} disabled={isDisabled}>
      Click me!
    </button>
  );
};
`}</code></pre>
    <p>{`Global state data is any data made available to components in the entire application.
Components that depend on a global state value subscribe to changes for that value and re-render themselves.
Most components do not depend on the entire global state.
Instead, a component only uses small pieces of the entire global state.`}</p>
    <p>{`Shopping cart data is an example of global state data that components in different levels of the application use and modify.`}</p>
    <h2 {...{
      "id": "common-state-management-technologies"
    }}>{`Common state management technologies`}</h2>
    <p>{`There are many libraries and framework features that implement state management.
This section describes two such technologies used in the PWA Studio project.`}</p>
    <h3 {...{
      "id": "redux"
    }}>{`Redux`}</h3>
    <p><a parentName="p" {...{
        "href": "https://redux.js.org/introduction/getting-started"
      }}>{`Redux`}</a>{` is a state management design pattern and library.
It promotes the idea of a global object tree that contains the state of the whole application.
This object is known as a `}<a parentName="p" {...{
        "href": "https://redux.js.org/glossary#store"
      }}>{`store`}</a>{`.`}</p>
    <p>{`The store is a read-only object, which can only be updated by dispatching a `}<a parentName="p" {...{
        "href": "https://redux.js.org/glossary#reducer"
      }}>{`reducer`}</a>{` function.
Reducer functions accept the current state and an `}<a parentName="p" {...{
        "href": "https://redux.js.org/basics/actions"
      }}>{`action`}</a>{` object as parameters and returns the next state.`}</p>
    <p>{`Application components are able to `}<a parentName="p" {...{
        "href": "https://redux.js.org/api/store#dispatchaction"
      }}>{`dispatch`}</a>{` various actions to update the state.
Components can also `}<a parentName="p" {...{
        "href": "https://redux.js.org/api/store#subscribelistener"
      }}>{`subscribe`}</a>{` to state changes to update their appearance or behavior.`}</p>
    <p>{`Early versions of PWA Studio used the Redux library directly as the primary mechanism for managing application state,
and the Redux pattern can be seen in hooks such as `}<a parentName="p" {...{
        "href": "https://github.com/magento/pwa-studio/blob/develop/packages/peregrine/lib/hooks/useRestResponse.js"
      }}><inlineCode parentName="a">{`useRestResponse()`}</inlineCode></a>{`.`}</p>
    <p>{`Currently, PWA Studio abstracts away its Redux implementation details using Peregrine hooks and context providers.
This opens up the possibility of the project replacing Redux in Peregrine with another state management library without breaking state dependent components, such as those in Venia.`}</p>
    <p>{`PWA Studio allows you to customize reducers and enhancers.
The following example uses `}<inlineCode parentName="p">{`combineReducers()`}</inlineCode>{` to combine the default Peregrine reducers with custom reducers specific to the project and uses the combined reducers when creating the Redux store.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// Example src/store.js file

import { combineReducers, createStore } from "redux";
import { enhancer, reducers } from "@magento/peregrine";

import myReducers from "./lib/reducers";

// You can add your own reducers here and combine them with the Peregrine exports.
const rootReducer = combineReducers({ ...reducers, ...myReducers });

export default createStore(rootReducer, enhancer);
`}</code></pre>
    <h3 {...{
      "id": "react-hooks"
    }}>{`React hooks`}</h3>
    <p>{`React version 16.8 introduced the concept of `}<a parentName="p" {...{
        "href": "https://reactjs.org/docs/hooks-intro.html"
      }}>{`hooks`}</a>{`.
Hooks allow `}<a parentName="p" {...{
        "href": "https://reactjs.org/docs/components-and-props.html#function-and-class-components"
      }}>{`function components`}</a>{` to manage their own internal state by letting them use the same component lifecycle features available to class components.`}</p>
    <p>{`Earlier versions of React only allowed class components to manage state,
so stateful classes often became complex and hard to understand.
Hooks help developers to decompose large components into smaller functions that are focused on specific logic, such as fetching data.`}</p>
    <p>{`Since PWA Studio favors using function components over classes, it uses many of React's `}<a parentName="p" {...{
        "href": "https://reactjs.org/docs/hooks-reference.html"
      }}>{`built-in hooks`}</a>{` in its Venia and Peregrine libraries.
The Peregrine library also provides `}<a parentName="p" {...{
        "href": "https://reactjs.org/docs/hooks-custom.html"
      }}>{`custom React hooks`}</a>{` for storefront developers.
These hooks contain common storefront logic such as state management.`}</p>
    <h2 {...{
      "id": "state-management-in-pwa-studio"
    }}>{`State management in PWA Studio`}</h2>
    <p>{`State management in PWA Studio is a mix of the Redux library, React hooks, and React context providers.
The Redux library is the underlying technology that powers state management behind the scenes, but
components do not interact with the global store directly.
Instead, components that need global state data use React hooks and context providers to read or update the current state.`}</p>
    <h3 {...{
      "id": "context-providers"
    }}>{`Context providers`}</h3>
    <p>{`React components look and behave as a result of their props.
Normally, this means an application needs to explicitly pass state data as a prop down the React application tree to components that need that data.
This is known as `}<em parentName="p">{`prop drilling`}</em>{`.`}</p>
    <p>{`To avoid prop drilling, React provides the `}<a parentName="p" {...{
        "href": "https://reactjs.org/docs/context.html"
      }}>{`Context`}</a>{` feature.
The Context feature allows an application to define a value and make it available to its descendants without passing it down the tree.`}</p>
    <p>{`A Context object contains a Provider and Consumer property.
A `}<inlineCode parentName="p">{`Context.Provider`}</inlineCode>{` component defines the shared data for its children, and
a corresponding `}<inlineCode parentName="p">{`Context.Consumer`}</inlineCode>{` acquires the data and subscribes to any changes.`}</p>
    <p>{`PWA Studio uses the Context feature to provide application state data to storefront components through the `}<a parentName="p" {...{
        "href": "https://github.com/magento/pwa-studio/blob/develop/packages/peregrine/lib/PeregrineContextProvider/peregrineContextProvider.js"
      }}><inlineCode parentName="a">{`PeregrineContextProvider`}</inlineCode></a>{` component.
Wrapping an application with the `}<inlineCode parentName="p">{`PeregrineContextProvider`}</inlineCode>{` lets its components access different slices of the entire application state.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// Example src/index.js

import React from 'react';
import ReactDOM from 'react-dom';

import { Adapter } from '@magento/venia-drivers';
import { PeregrineContextProvider } from '@magento/peregrine';

import store from './store'; // This was defined in the previous example
import MyApplication from \`./src/components/MyApplication\`;

const apiBase = new URL('/graphql', location.origin).toString();

ReactDOM.render(
    <Adapter
        apiBase={apiBase}
        apollo={{ link: authLink.concat(Adapter.apolloLink(apiBase)) }}
        store={store}
    >
        <PeregrineContextProvider>
            <MyApplication />
        </PeregrineContextProvider>
    </Adapter>,
    document.getElementById('root')
);

`}</code></pre>
    <h3 {...{
      "id": "global-state-slices"
    }}>{`Global state slices`}</h3>
    <p>{`Peregrine exposes global state data in slices through the `}<inlineCode parentName="p">{`PeregrineContextProvider`}</inlineCode>{` component and custom React hooks.
A state data slice is a subset of values from the global state.
Each slice contains data about a specific part of the application, such as the shopping cart state or user session state.`}</p>
    <p>{`To access a global state slice, wrap the `}<inlineCode parentName="p">{`PeregrineContextProvider`}</inlineCode>{` around the main application (as shown in the previous example).`}</p>
    <p>{`Next, import the appropriate `}<a parentName="p" {...{
        "href": "https://github.com/magento/pwa-studio/tree/develop/packages/peregrine/lib/context"
      }}>{`context hook`}</a>{` and decompose the array returned by the hook function call.
The decomposed array yields the state data and an API object to update that state.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// Example src/components/MyWelcomeMessage/myWelcomeMessage.js

import { useUserContext } from "@magento/peregrine/lib/context/user";

const MyWelcomeMessage = () => {
  const [userContext, userContextApi] = useUserContext();

  const { isSignedIn, currentUser } = userContext;
  const { firstname, lastname } = currentUser;

  if (isSignedIn) {
    return (
      <span>
        Welcome {firstname} {lastname}!
      </span>
    );
  }

  return null;
};

export default MyWelcomeMessage;
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      